// Pagina publica a la que apunta el QR impreso en la ficha medica (ver ficha_medica.html).
// No requiere sesion previa para ENTRAR a la pagina, pero SI exige login (email + password)
// antes de mostrar cualquier dato de la evaluacion -> GET /evaluaciones/verificar/{valor}

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { UserContext } from '../contexts/UserContext';
import { login, logout, fetchCurrentUser, getVerificacionEva } from '../services/api';
import { formatRut } from '../utils/rut';
import styles from '../styles/login.module.css';
import '../styles/styles.css';

const VerificarFicha = () => {
  const { valor } = useParams();
  const { setCurrentUser } = useContext(UserContext);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [enviandoLogin, setEnviandoLogin] = useState(false);

  const [necesitaLogin, setNecesitaLogin] = useState(null);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);

  const mostrarFondoLogin = cargando || necesitaLogin === null || necesitaLogin === true;
  useEffect(() => {
    if (mostrarFondoLogin) {
      document.body.classList.add(styles.loginBg);
    } else {
      document.body.classList.remove(styles.loginBg);
    }
    return () => document.body.classList.remove(styles.loginBg);
  }, [mostrarFondoLogin]);

  const consultarFicha = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const res = await getVerificacionEva(valor);
      setDatos(res.data);
      setNecesitaLogin(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setNecesitaLogin(true);
      } else if (status === 403) {
        setNecesitaLogin(true);
        setLoginError('⚠️ La cuenta con la que iniciaste sesión no tiene acceso a esta ficha.');
      } else if (status === 404) {
        setNecesitaLogin(false);
        setError('No se encontró una ficha generada para esta evaluación.');
      } else {
        setNecesitaLogin(false);
        setError('⚠️ Error al verificar la ficha. Intenta nuevamente más tarde.');
      }
    } finally {
      setCargando(false);
    }
  }, [valor]);

  useEffect(() => {
    consultarFicha();
  }, [consultarFicha]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setEnviandoLogin(true);
    try {
      await login({ email, password });
      const res = await fetchCurrentUser();
      setCurrentUser(res?.data ?? null);
      await consultarFicha();
    } catch (err) {
      setLoginError('⚠️ Credenciales inválidas.');
    } finally {
      setEnviandoLogin(false);
    }
  };

  const handleCambiarCuenta = async () => {
    try {
      await logout();
    } catch (err) {
      // noop, igual limpiamos el estado local para reintentar login
    }
    setCurrentUser(null);
    setDatos(null);
    setLoginError('');
    setNecesitaLogin(true);
  };

  if (cargando || necesitaLogin === null) {
    return (
      <div className={styles.loginContainer}>
        <p>Verificando ficha…</p>
      </div>
    );
  }

  if (necesitaLogin) {
    return (
      <div className={styles.loginContainer}>
          {loginError && (
            <div className={styles.flashError}>
              <span className={styles.icon}>⚠️</span>
              {loginError.replace('⚠️ ', '')}
              <span className={styles.close} onClick={() => setLoginError('')}>×</span>
            </div>
          )}
          <img src="/static/logo.png" alt="Logo XIOS" />
          <p>Ingresa tu cuenta para verificar esta ficha médica.</p>
          <form onSubmit={handleLogin}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Usuario</label>
              <input
                type="text"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.formControl}
                autoComplete="username"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">Contraseña</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.formControl}
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={enviandoLogin}
              className={`${styles.btnPrimary} ${styles.btnActive}`}
            >
              {enviandoLogin ? 'Verificando…' : 'Entrar y ver ficha'}
            </button>
          </form>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper">
        <p className="required-text">{error}</p>
      </div>
    );
  }

  if (!datos) return null;

  return (
    <div className="content-wrapper">
      <div className="container" style={{ maxWidth: '700px', margin: '40px auto' }}>
        <h1>Verificación de Ficha Médica</h1>
        <p style={{ color: '#666' }}>
          Los datos a continuación corresponden a la ficha médica emitida por Centro Médico XIOS.
        </p>

        <table className="table">
          <tbody>
            <tr>
              <th>ID Evaluación</th>
              <td>{datos.id_eva}</td>
            </tr>
            <tr>
              <th>Paciente</th>
              <td>{datos.nombre_paciente}</td>
            </tr>
            <tr>
              <th>RUT</th>
              <td>{datos.rut_paciente ? formatRut(datos.rut_paciente) : '-'}</td>
            </tr>
            <tr>
              <th>Batería(s)</th>
              <td>{datos.nombres_bat?.length ? datos.nombres_bat.join(', ') : 'Sin baterías asignadas'}</td>
            </tr>
            <tr>
              <th>Fecha de emisión</th>
              <td>{datos.fecha_creacion_ficha ? moment(datos.fecha_creacion_ficha).format('DD-MM-YYYY HH:mm') : '-'}</td>
            </tr>
            <tr>
              <th>Vigente hasta</th>
              <td>{datos.fecha_vencimiento ? moment(datos.fecha_vencimiento).format('DD-MM-YYYY') : '-'}</td>
            </tr>
          </tbody>
        </table>

        <h2>Conclusión Médica</h2>
        {datos.conclusion_medica ? (
          <table className="table">
            <tbody>
              <tr>
                <th>Resumen</th>
                <td>{datos.conclusion_medica.resumen || 'N/A'}</td>
              </tr>
              <tr>
                <th>Recomendaciones</th>
                <td>{datos.conclusion_medica.recomendaciones || 'N/A'}</td>
              </tr>
              <tr>
                <th>Indicaciones</th>
                <td>{datos.conclusion_medica.indicaciones || 'N/A'}</td>
              </tr>
              <tr>
                <th>Vigencia</th>
                <td>{datos.conclusion_medica.vigencia} meses</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="required-text">No hay conclusión médica registrada para esta evaluación.</p>
        )}

        <button type="button" className="btn-secondary" onClick={handleCambiarCuenta} style={{ marginTop: '20px' }}>
          Salir / verificar con otra cuenta
        </button>
      </div>
    </div>
  );
};

export default VerificarFicha;
